import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from 'react-router-dom'
import { FC, useEffect } from 'react'
import { Box, CssBaseline, ThemeProvider } from '@mui/material'
import { darkTheme, lightTheme } from './components/style/mui-theme'
import { ThemeContextProvider, useThemeMode } from './context/ThemeContext'
import { styled } from '@mui/material/styles'
import Header from './areas/layout/header/header'
import Footer from './areas/layout/footer/footer'
import HomePage from './pages/Home/home-page'
import DiscoverPage from './pages/Discover/discover-page'
import SearchResultsPage from './pages/SearchResults/search-results'
import MovieDetailPage from './pages/Movies/movie-detail-page'
import ShowDetailPage from './pages/Shows/show-detail-page'
import MoviesPage from './pages/Movies/movies-page'
import ShowsPage from './pages/Shows/shows-page'
import SeasonPage from './pages/Shows/season-page'
import ActorsPage from './pages/Actors/actors-page'
import ActorDetailPage from './pages/Actors/actor-detail-page'
import SettingsPage from './pages/Settings/settings-page'
import AdminPage from './pages/Admin/admin-page'
import { ProfilePage } from './pages/Profile/profile-page'
import AboutPage from './pages/About/about-page'
import GuidePage from './pages/Guide/guide-page'
import HelpPage from './pages/Help/help-page'
import { RouteNotFound } from './pages/Status/status-page'
import RandomizerPage from './pages/Randomizer/randomizer-page'
import ScrollToTop from './areas/layout/scroll-to-top-btn'
import ScrollRestoration from './areas/layout/scroll-restoration'
import PageViewTracker from './analytics/page-view-tracker'
import CollectionDetailPage from './pages/Collections/collection-detail-page'
import UserProfilePage from './pages/Users/user-profile-page'
import MediaStateProvider from './context/media-state-context'
import BrowseHistoryProvider from './context/browse-history-context'
import RecentDrawer from './components/recent-drawer/recent-drawer'
import { SnackbarProvider } from './context/snackbar-context'
import { ErrorBoundary } from './components/ErrorBoundary'
import { POST_LOGOUT_PATH_KEY, useAuth } from './context/AuthContext'
import settingsApi from './client/settings-api'
import DeletedAccountModal from './components/deleted-account-modal'
import OnboardingModal from './components/onboarding-modal'

const StyledThemeBox = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
}))

const HeaderSpacer = styled(Box)(({ theme }) => ({
  height: 96,
  [theme.breakpoints.down('sm')]: {
    height: 80,
  },
}))

// The header is fixed, so it no longer reserves space in flow. Plain pages need
// that height back, but pages with a top hero backdrop (Home slider, detail
// pages) deliberately bleed up behind the transparent-bottomed header.
const HERO_ROUTE_PREFIXES = ['/details/', '/collection/']

const pageBleedsBehindHeader = (path: string): boolean => {
  if (path === '/') return true
  if (HERO_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix))) return true
  // Actor detail (/actors/:id) has a hero; the actors list (/actors/popular) does not.
  if (path.startsWith('/actors/') && path !== '/actors/popular') return true
  // Season detail (/shows/:id/season/:n) has a hero; the shows list (/shows/:category) does not.
  if (path.startsWith('/shows/') && path.includes('/season/')) return true
  return false
}

const HeaderOffset: FC = () => {
  const location = useLocation()
  if (pageBleedsBehindHeader(location.pathname)) return null
  return <HeaderSpacer />
}

// Auth0 logout always returns to the origin; this sends the user back to the
// page they logged out from (stashed in sessionStorage by AuthContext.logout).
const RestorePostLogoutPath: FC = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const path = sessionStorage.getItem(POST_LOGOUT_PATH_KEY)
    if (path) {
      sessionStorage.removeItem(POST_LOGOUT_PATH_KEY)
      if (path !== window.location.pathname + window.location.search) {
        navigate(path, { replace: true })
      }
    }
  }, [navigate])

  return null
}

// After login (or account switch), pull the signed-in account's settings so
// the theme and the settings cache match that user everywhere immediately,
// instead of only after the first visit to the Settings page.
const AccountThemeSync: FC = () => {
  const { isAuthenticated, user, getAccessToken } = useAuth()
  const { preference, setPreference } = useThemeMode()

  useEffect(() => {
    if (!isAuthenticated || !user) return
    let cancelled = false
    ;(async () => {
      try {
        const token = await getAccessToken()
        const response = await settingsApi.get(token)
        if (cancelled) return
        localStorage.setItem('settings-cache', JSON.stringify(response.data))
        const theme = response.data.theme
        if (
          (theme === 'dark' || theme === 'light' || theme === 'system') &&
          theme !== preference
        ) {
          setPreference(theme)
        }
      } catch {
        /* keep the current theme */
      }
    })()
    return () => {
      cancelled = true
    }
    // Re-sync when a different account signs in - not on every theme flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id])

  return null
}

// Sends fresh visits to the user's preferred landing page (Settings → Preferences).
// Only acts on the root path so deep links and refreshes elsewhere are untouched.
const DefaultLandingRedirect: FC = () => {
  const navigate = useNavigate()

  useEffect(() => {
    if (window.location.pathname !== '/') return
    try {
      const cached = localStorage.getItem('settings-cache')
      if (!cached) return
      const landing = JSON.parse(cached)?.defaultLandingPage
      if (landing === 'discover') navigate('/discover', { replace: true })
      else if (landing === 'profile') navigate('/profile', { replace: true })
    } catch {
      /* corrupt cache - stay on home */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}

const ThemedApp: FC = () => {
  const { mode } = useThemeMode()

  return (
    <ThemeProvider theme={mode === 'dark' ? darkTheme : lightTheme}>
      <CssBaseline />
      <BrowserRouter>
        <StyledThemeBox>
          <SnackbarProvider>
            <ErrorBoundary
              fallbackTitle='Something went wrong'
              fallbackMessage='An unexpected error occurred. Please refresh the page to continue.'
              onRetry={() => window.location.reload()}
            >
              <MediaStateProvider>
                <BrowseHistoryProvider>
                  <RestorePostLogoutPath />
                  <AccountThemeSync />
                  <DeletedAccountModal />
                  <OnboardingModal />
                  <DefaultLandingRedirect />
                  <ScrollRestoration />
                  <PageViewTracker />
                  <Header />
                  <HeaderOffset />
                  <RecentDrawer />
                  <Routes>
                    <Route path='/' element={<HomePage />} />
                    <Route path='/discover' element={<DiscoverPage />} />
                    <Route path='/randomizer' element={<RandomizerPage />} />
                    <Route
                      path='/details/movie/:id'
                      element={<MovieDetailPage />}
                    />
                    <Route
                      path='/details/show/:id'
                      element={<ShowDetailPage />}
                    />
                    <Route
                      path='/shows/:id/season/:seasonNumber'
                      element={<SeasonPage />}
                    />
                    <Route
                      path='/collection/:id'
                      element={<CollectionDetailPage />}
                    />
                    <Route path='/actors/popular' element={<ActorsPage />} />
                    <Route path='/actors/:id' element={<ActorDetailPage />} />
                    <Route path='/search' element={<SearchResultsPage />} />
                    <Route path='/movies/:category' element={<MoviesPage />} />
                    <Route path='/shows/:category' element={<ShowsPage />} />
                    <Route path='/user/:id' element={<UserProfilePage />} />
                    <Route path='/profile' element={<ProfilePage />} />
                    <Route
                      path='/profile/settings'
                      element={<SettingsPage />}
                    />
                    <Route path='/admin' element={<AdminPage />} />
                    <Route path='/about' element={<AboutPage />} />
                    <Route path='/guide' element={<GuidePage />} />
                    <Route path='/help' element={<HelpPage />} />
                    <Route path='*' element={<RouteNotFound />} />
                  </Routes>
                  <ScrollToTop />
                  <Footer />
                </BrowseHistoryProvider>
              </MediaStateProvider>
            </ErrorBoundary>
          </SnackbarProvider>
        </StyledThemeBox>
      </BrowserRouter>
    </ThemeProvider>
  )
}

const Application: FC = () => {
  return (
    <ThemeContextProvider>
      <ThemedApp />
    </ThemeContextProvider>
  )
}

export { Application }
